import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { ChangeEvent, FocusEvent, useEffect, useState } from "react";

import { ModelState } from "../../interfaces/modelState";
import { Tooltip } from "../tooltip/Tooltip";
import { hasValidationMessage } from "../validationMessage/hasValidationMessage";
import { ValidationMessage } from "../validationMessage/ValidationMessage";
import ConditionalWrapper from "./ConditionalWrapper";
import sanitizeInput, { Sanitizers } from "./sanitizeInput";
import unobtrusiveValidation from "./unobtrusiveValidation";
import useFieldData from "./useFieldData";
import useFieldName from "./useFieldName";
import { Validators } from "./validator/validators";

interface Props {
  fieldData: Record<string, Field<string>>;
  name: string;
  fieldName?: string;
  modelState?: ModelState | null;
  setModelState?: (modelState: ModelState) => void;
  inputValue?: number;
  id?: string;
  sanitize?: Sanitizers<number | string | undefined>;
  validators: Validators<number>;
  onChange: (value?: number) => void;
  propertyName: string;
  excludeRow?: boolean;
  widthClassName?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  defaultInputValue?: number;
  notRange?: boolean;
}

const NumberInput = (props: Props) => {
  const { name, fieldName } = useFieldName(props.name, props.fieldName);
  const { heading, placeholder, prefix, suffix, info, tooltip } = useFieldData(props.fieldData, name);
  const [isDirty, setDirty] = useState(Boolean(props.inputValue));
  const sanitizedInput = sanitizeInput(props.inputValue, props.sanitize) ?? "";
  const modelState = props.modelState;

  const { defaultInputValue, inputValue, onChange } = props;
  useEffect(() => {
    if (defaultInputValue && !inputValue) {
      onChange(defaultInputValue);
    }
  }, [defaultInputValue, inputValue, onChange]);

  const handleNumberInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\./g, "");
    const re = /^[0-9\b]+$/;
    if (re.test(value)) {
      onChange(parseFloat(value));
    }
    if (value === "") {
      onChange(undefined);
    }
  };

  const handleNumberBlur = (e: FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\./g, "");
    const re = /^[0-9\b]+$/;
    let dirty = isDirty;
    if (!dirty && value) {
      setDirty(true);
      dirty = true;
    }

    const { min, max } = props;

    if (inputValue === undefined || re.test(value)) {
      let number = parseFloat(value);

      if (max && number > max) {
        number = max;
      }

      if (min && number < min) {
        number = min;
      }

      if (dirty && props.setModelState) {
        props.setModelState(unobtrusiveValidation(props.validators, modelState, props.propertyName, number));
      }

      props.onChange(!isNaN(number) ? number : undefined);
    }
  };

  const id = props.id || fieldName;
  const hasError = hasValidationMessage(modelState, props.propertyName);
  const errorId = `${id}-error`;

  return (
    <ConditionalWrapper condition={!props.excludeRow} wrap={(children) => <div className="form-row">{children}</div>}>
      <div
        className={`form-item ${props.notRange ? "" : "range"} ${
          props.widthClassName ? props.widthClassName : "medium"
        }`}
      >
        <div className={`form-input ${props.notRange ? "" : "range"}`}>
          <div className="form-label">
            <label htmlFor={id}>{heading.value}</label>
            <Tooltip fieldName={fieldName} value={tooltip.value} />
          </div>
          {prefix.value ? <div className="prefix">{prefix.value}</div> : <></>}
          <input
            id={id}
            disabled={props.disabled}
            required={props.required}
            aria-invalid={hasError}
            aria-errormessage={hasError ? errorId : undefined}
            placeholder={placeholder.value}
            type="tel"
            lang="nl-NL"
            autoComplete="off"
            min={props.min}
            max={props.max}
            value={sanitizedInput}
            onChange={handleNumberInput}
            onBlur={handleNumberBlur}
          />
          {suffix.value ? <div className="suffix">{suffix.value}</div> : <></>}
        </div>
        {info.value && <span>{info.value}</span>}
      </div>
      <ValidationMessage modelState={modelState} property={props.propertyName} id={errorId} />
    </ConditionalWrapper>
  );
};

export default NumberInput;
